/**
 * useThemeV2Actions - Hook for V2 theme CRUD operations
 *
 * Provides create, update, delete, and clone operations with loading states.
 * Uses ThemeV2Service (themesV2/ collection).
 */

import { useState, useCallback } from 'react';
import { ThemeV2Service } from '@modules/firebase-core/services/ThemeV2Service';
import type { CreateThemeV2Input, UpdateThemeV2Input } from '../types/themesV2.types';

export interface UseThemeV2ActionsReturn {
  /** Create a new V2 theme */
  createTheme: (data: CreateThemeV2Input) => Promise<string>;
  /** Update an existing V2 theme */
  updateTheme: (themeId: string, data: UpdateThemeV2Input) => Promise<void>;
  /** Delete a V2 theme */
  deleteTheme: (themeId: string) => Promise<void>;
  /** Clone a V2 theme */
  cloneTheme: (themeId: string, newName?: string) => Promise<string>;
  /** Whether an operation is in progress */
  loading: boolean;
  /** Error from last operation */
  error: Error | null;
}

/**
 * useThemeV2Actions - Provides V2 theme CRUD operations
 */
export function useThemeV2Actions(): UseThemeV2ActionsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const themeService = new ThemeV2Service();

  const createTheme = useCallback(async (data: CreateThemeV2Input): Promise<string> => {
    try {
      setLoading(true);
      setError(null);

      const theme = await themeService.create(data);
      return theme.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create V2 theme');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTheme = useCallback(async (themeId: string, data: UpdateThemeV2Input): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await themeService.update(themeId, data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update V2 theme');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTheme = useCallback(async (themeId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await themeService.delete(themeId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete V2 theme');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cloneTheme = useCallback(async (themeId: string, newName?: string): Promise<string> => {
    try {
      setLoading(true);
      setError(null);

      const name = newName || 'Copy of Theme';
      const clonedTheme = await themeService.clone(themeId, name);
      return clonedTheme.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to clone V2 theme');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createTheme,
    updateTheme,
    deleteTheme,
    cloneTheme,
    loading,
    error,
  };
}

export default useThemeV2Actions;
